import { PLAYBACK_QUEUE_CHUNK_SIZE } from '../ports/playback-event-store.js';
import { chunkIndexOf, chunkRange } from './playback-queue-chunks.js';
import { consoleLogger } from '../ports/logger.js';
import { InvalidPlaybackQueueSnapshotError, } from '../ports/playback-event-store.js';
/** The API accepts at most 100 events per request. */
export const PLAYBACK_EVENT_BATCH_LIMIT = 100;
/** Oldest events are dropped above this, so a long offline stretch can't fill storage. */
export const PLAYBACK_EVENT_CAPACITY = 50_000;
/** A batch that fails this many times in a row is set aside and counted as dropped. */
export const PLAYBACK_BATCH_MAX_ATTEMPTS = 10;
/**
 * crypto.randomUUID only exists from Chrome 92; Samsung/Tizen browsers are older.
 * Falls back to a v4 UUID from getRandomValues, which the server's uuid check accepts.
 */
export function newClientEventId() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function')
        return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function')
        c.getRandomValues(bytes);
    else
        for (let i = 0; i < 16; i += 1)
            bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/**
 * Queue of playback events waiting to be sent. Events are only removed after the server
 * confirms them (ack), so a failed send keeps them for the next sync.
 */
export class PlaybackEventBuffer {
    store;
    options;
    events = [];
    /** Sequence number of events[0]; events[i] has headSeq + i. Chunk n holds n*1000 to n*1000+999. */
    headSeq = 0;
    /** What the store holds now; null while it holds no chunked queue (the next save writes it all). */
    stored = null;
    /** Events left from the middle of the queue, so positions shifted: rewrite every chunk once. */
    rewriteAll = false;
    writes = Promise.resolve();
    dirty = false;
    writing = false;
    queueId = newClientEventId();
    droppedEvents = 0;
    acknowledgedDroppedEvents = 0;
    /** False until the stored queue has loaded; nothing is saved before that. */
    loaded = false;
    /** Failures in a row for the batch at the front of the queue. Saved with the queue. */
    headFailure;
    logger;
    constructor(store, options = {}) {
        this.store = store;
        this.options = options;
        this.logger = options.logger ?? consoleLogger;
    }
    /** Restores events saved before a reload. Call once at start. */
    async init() {
        try {
            const saved = await this.store.load();
            const restored = Array.isArray(saved) ? saved : saved.events;
            if (!Array.isArray(saved)) {
                this.queueId = saved.queueId;
                this.droppedEvents = saved.droppedEvents;
                this.acknowledgedDroppedEvents = saved.acknowledgedDroppedEvents;
                const bf = saved.batchFailure;
                if (bf &&
                    typeof bf.firstClientEventId === 'string' &&
                    Number.isSafeInteger(bf.attempts) &&
                    bf.attempts > 0) {
                    this.headFailure = { firstClientEventId: bf.firstClientEventId, attempts: bf.attempts };
                }
                if (saved.version === 2) {
                    this.headSeq = saved.headSeq;
                    this.stored = { headSeq: saved.headSeq, nextSeq: saved.nextSeq };
                }
            }
            // Events recorded before the load finished go after the restored ones.
            const ids = new Set(this.events.map((e) => e.clientEventId));
            const kept = restored.filter((e) => !ids.has(e.clientEventId));
            if (kept.length !== restored.length)
                this.rewriteAll = true;
            this.events = [...kept, ...this.events];
            this.trim();
            this.loaded = true;
            this.persist();
        }
        catch (error) {
            if (error instanceof InvalidPlaybackQueueSnapshotError) {
                // Unreadable stored queue: retrying won't fix it, and staying unloaded would stop
                // every future save. Start a fresh queue, count what was lost, and save again.
                this.logger.warn('Stored playback queue was invalid, starting a fresh queue', {
                    error: error.message,
                    discardedEvents: error.discardedEvents,
                });
                this.queueId = newClientEventId();
                this.droppedEvents = Math.min(Number.MAX_SAFE_INTEGER, error.discardedEvents);
                this.acknowledgedDroppedEvents = 0;
                this.headFailure = undefined;
                this.stored = null;
                this.trim();
                this.loaded = true;
                this.persist();
                return;
            }
            this.logger.warn('Playback event queue could not be loaded', {
                error: error instanceof Error ? error.message : String(error),
            });
            throw error;
        }
    }
    record(input) {
        const event = {
            ...input,
            clientEventId: (this.options.newId ?? newClientEventId)(),
        };
        this.events.push(event);
        this.trim();
        this.persist();
        return event;
    }
    /** The oldest events, without removing them. */
    takeBatch(limit = PLAYBACK_EVENT_BATCH_LIMIT) {
        return this.events.slice(0, Math.min(limit, PLAYBACK_EVENT_BATCH_LIMIT));
    }
    /** Removes events the server has confirmed. */
    ack(clientEventIds) {
        if (clientEventIds.length === 0)
            return;
        this.headFailure = undefined;
        this.remove(new Set(clientEventIds));
        this.persist();
    }
    /** Drops everything, e.g. when the device is unpaired (events belong to the old pairing). */
    clear() {
        this.headSeq += this.events.length;
        this.events = [];
        this.queueId = newClientEventId();
        this.droppedEvents = 0;
        this.acknowledgedDroppedEvents = 0;
        this.headFailure = undefined;
        this.persist();
    }
    pendingQueueLoss() {
        if (this.droppedEvents <= this.acknowledgedDroppedEvents)
            return undefined;
        return { queueId: this.queueId, droppedEvents: this.droppedEvents };
    }
    acknowledgeQueueLoss(ack) {
        if (ack.queueId !== this.queueId ||
            !Number.isSafeInteger(ack.droppedEvents) ||
            ack.droppedEvents < 0)
            return;
        this.acknowledgedDroppedEvents = Math.max(this.acknowledgedDroppedEvents, Math.min(ack.droppedEvents, this.droppedEvents));
        this.persist();
    }
    /**
     * Counts a failed send of the batch at the front of the queue. After `limit` failures
     * in a row the batch is set aside: its events leave the queue and count as dropped, so
     * one batch the server can never accept can't block every later event.
     * Returns true when the batch was dropped.
     */
    recordBatchFailure(batch, limit = PLAYBACK_BATCH_MAX_ATTEMPTS) {
        const first = batch[0]?.clientEventId;
        if (!first)
            return false;
        if (this.headFailure?.firstClientEventId === first)
            this.headFailure.attempts += 1;
        else
            this.headFailure = { firstClientEventId: first, attempts: 1 };
        if (this.headFailure.attempts < limit) {
            this.persist(); // so a reload doesn't reset the count
            return false;
        }
        const removed = this.remove(new Set(batch.map((e) => e.clientEventId)));
        this.droppedEvents = Math.min(Number.MAX_SAFE_INTEGER, this.droppedEvents + removed);
        this.headFailure = undefined;
        this.persist();
        return true;
    }
    size() {
        return this.events.length;
    }
    /** Resolves when every write so far has finished (for tests and shutdown). */
    flushWrites() {
        return this.writes;
    }
    /**
     * Removes these events. Leaving from the front only moves headSeq; leaving from the
     * middle shifts positions, so every chunk is rewritten once. Returns how many left.
     */
    remove(ids) {
        let prefix = 0;
        while (prefix < this.events.length && ids.has(this.events[prefix].clientEventId))
            prefix += 1;
        const rest = this.events.slice(prefix);
        const kept = rest.filter((e) => !ids.has(e.clientEventId));
        if (kept.length !== rest.length)
            this.rewriteAll = true;
        this.events = kept;
        this.headSeq += prefix;
        return prefix + (rest.length - kept.length);
    }
    trim() {
        const capacity = this.options.capacity ?? PLAYBACK_EVENT_CAPACITY;
        if (!Number.isSafeInteger(capacity) || capacity < 1) {
            throw new Error('Playback queue capacity must be a positive safe integer');
        }
        const excess = this.events.length - capacity;
        if (excess > 0) {
            let drop = excess;
            if (capacity >= 2 * PLAYBACK_QUEUE_CHUNK_SIZE) {
                // Trim whole chunks from the head: one chunk delete, instead of rewriting the
                // head chunk on every event once the queue is full.
                const newHead = (chunkIndexOf(this.headSeq + excess - 1) + 1) * PLAYBACK_QUEUE_CHUNK_SIZE;
                drop = Math.min(this.events.length, newHead - this.headSeq);
            }
            this.droppedEvents = Math.min(Number.MAX_SAFE_INTEGER, this.droppedEvents + drop);
            this.events = this.events.slice(drop);
            this.headSeq += drop;
        }
    }
    /**
     * Only what changed since the last save: the meta, the chunk new events went into (and any
     * after it), the head chunk once when the front moved, and deletes for chunks now empty.
     */
    buildWrite(full) {
        const size = PLAYBACK_QUEUE_CHUNK_SIZE;
        const headSeq = this.headSeq;
        const nextSeq = headSeq + this.events.length;
        const { first, last } = chunkRange(headSeq, nextSeq);
        const stored = this.stored;
        const changed = new Set();
        if (full || !stored) {
            for (let c = first; c <= last; c += 1)
                changed.add(c);
        }
        else {
            // Head moved inside a chunk (a partial ack): rewrite that chunk once. On a chunk
            // boundary the first chunk is unchanged, so nothing to rewrite.
            if (headSeq !== stored.headSeq && headSeq % size !== 0 && first <= last)
                changed.add(first);
            if (nextSeq > stored.nextSeq) {
                for (let c = Math.max(first, chunkIndexOf(stored.nextSeq)); c <= last; c += 1)
                    changed.add(c);
            }
        }
        const deleteChunks = [];
        if (stored) {
            const old = chunkRange(stored.headSeq, stored.nextSeq);
            for (let c = old.first; c <= old.last; c += 1) {
                if (c < first || c > last)
                    deleteChunks.push(c);
            }
        }
        const chunks = [...changed]
            .sort((a, b) => a - b)
            .map((index) => {
            const from = Math.max(index * size, headSeq) - headSeq;
            const to = Math.min((index + 1) * size, nextSeq) - headSeq;
            return { index, events: this.events.slice(from, to).map((event) => ({ ...event })) };
        });
        return {
            meta: {
                version: 2,
                queueId: this.queueId,
                droppedEvents: this.droppedEvents,
                acknowledgedDroppedEvents: this.acknowledgedDroppedEvents,
                ...(this.headFailure ? { batchFailure: { ...this.headFailure } } : {}),
                headSeq,
                nextSeq,
            },
            chunks,
            deleteChunks,
        };
    }
    persist() {
        this.dirty = true;
        // Nothing is saved until the stored queue has loaded, so a failed load
        // can't overwrite it with an empty queue.
        if (!this.loaded || this.writing)
            return;
        this.writing = true;
        this.writes = Promise.resolve().then(async () => {
            try {
                while (this.dirty) {
                    this.dirty = false;
                    // Captured when a write starts: only the chunks that changed, not the whole queue.
                    const full = this.rewriteAll || this.stored === null;
                    this.rewriteAll = false;
                    const write = this.buildWrite(full);
                    try {
                        await this.store.save(write);
                    }
                    catch (error) {
                        if (full)
                            this.rewriteAll = true;
                        throw error;
                    }
                    this.stored = { headSeq: write.meta.headSeq, nextSeq: write.meta.nextSeq };
                }
            }
            catch (error) {
                this.dirty = true;
                this.logger.warn('Playback event queue could not be saved', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
            finally {
                this.writing = false;
            }
        });
    }
}
//# sourceMappingURL=playback-event-buffer.js.map